<template>
	<div v-click-outside="clickOutside" class="searcher">
		<div class="searcher__search-side search-side">
			<selector
				v-model="searchType"
				:option-list="searchTypeList"
				:is-v3="true"
				class="search-side__select"
				@input="inputSearchType"
			/>
			<div class="search-side__input-box">
				<input
					ref="searcher"
					class="search-side__input-box__input"
					type="text"
					placeholder="제품 검색"
					@input="onInput"
				/>
				<ul
					v-if="resultList.length"
					class="search-side__input-box__list result"
				>
					<li
						v-for="item in resultList"
						:key="item.id"
						ref="listItemElem"
					>
						<button
							:class="[
								'result__item__select-btn',
								{
									'result__item__select-btn--discontinue':
										item.isDiscontinue,
								},
							]"
							@click="selecting(item.id)"
						>
							<span class="result__item__select-btn__text">{{
								item.title
							}}</span>
						</button>
					</li>
				</ul>
			</div>
		</div>
		<div class="searcher__selected-side selected-side">
			<input
				v-model="selectedTitle"
				:class="{ 'selected-side__input--selected': selectedId }"
				type="text"
				readonly
			/>
			<button
				v-if="selectedId"
				class="btn-delete selected-side__delete-btn"
				type="button"
				@click="deleting"
			>
				삭제
			</button>
		</div>
	</div>
</template>
<script>
	import debounce from 'lodash/debounce';
	import { useObservers } from '@/util';
	import ClickOutside from 'vue-click-outside';
	import Selector from '@/components/Selector';

	export default {
		name: 'ProductSearcher',
		directives: {
			ClickOutside,
		},
		components: {
			Selector,
		},
		data() {
			return {
				searchTypeList: [
					{ value: 'idProduct', name: '제품번호' },
					{ value: 'searchWord', name: '제품명' },
				],
				searchType: 'idProduct',
				intersection: useObservers().intersection,
				disconnect: null,
				maxOffset: 0,
				limit: 100,
				offset: 0,
				searchWord: '',
				resultList: [],
				selectedId: null,
				selectedTitle: '',
				observer: null,
			};
		},
		beforeDestroy() {
			if (this.disconnect) {
				this.disconnect();
			}
		},
		methods: {
			inputSearchType() {
				this.$refs.searcher.value = '';
				this.resultList = [];
			},
			clickOutside() {
				this.resultList = [];
			},
			selecting(id) {
				this.selectedId = id;
				this.selectedTitle = id
					? this.resultList.filter(item => item.id === id)[0].title
					: '';
				this.$emit('change', {
					id,
					title: this.selectedTitle,
				});
				this.resultList = [];
			},
			init() {
				this.resultList = [];
				this.selectedId = null;
				this.selectedTitle = '';
			},
			setObserver(listElem = []) {
				if (this.maxOffset > this.offset && listElem.length) {
					const lastIdx = listElem.length - 1;
					this.disconnect = this.intersection(
						listElem[lastIdx],
						() => {
							this.getList();
						}
					);
				}
			},
			async getList() {
				try {
					const params = {
						limit: this.limit,
						offset: this.offset,
					};
					params[this.searchType] = this.searchWord;
					const { data } = await this.$axiosAdmin2.get(
						`/api/products`,
						{ params }
					);
					const list = data.data.map(item => ({
						id: item.idProduct,
						title: `(${item.idProduct}) [${item.brandTitle}] ${item.productTitle}`,
						isDiscontinue: item.isDiscontinue,
					}));
					this.resultList = this.offset
						? [...this.resultList, ...list]
						: list;
					this.maxOffset = data.limit * data.pageTotal;
					this.offset += this.limit;
					await this.$nextTick();
					this.setObserver(this.$refs.listItemElem);
				} catch (err) {
					console.error(
						'제품검색에 오류가 발생했습니다. 개발팀에 문의하세요.'
					);
				}
			},
			onInput: debounce(function ({ target }) {
				this.searchWord = target.value;
				this.offset = 0;
				if (this.searchWord === '') {
					this.resultList = [];
					return;
				}
				this.getList();
			}, 700),
			deleting() {
				this.init();
				this.$refs.searcher.value = '';
				this.$emit('change', { id: null, title: '' });
			},
			async setValue(id) {
				this.searchWord = id;
				this.offset = 0;
				await this.getList();
				const targetObj = this.resultList.filter(
					item => item.id === id
				)[0];
				if (targetObj) {
					this.selecting(id);
				} else {
					alert(`매핑된 제품을 찾을 수 없습니다. 상품ID: ${id}`);
				}
			},
		},
	};
</script>
<style lang="scss" scoped>
	DIV.searcher {
		width: 100%;

		.search-side,
		.selected-side {
			display: flex;
			width: 100%;
			height: 50%;
		}

		.search-side__select {
			flex-shrink: 0;
		}

		.search-side__input-box {
			position: relative;
			width: 100%;
		}

		.search-side__input-box__input {
			&:focus {
				border-color: lightblue;
			}

			&:focus + .result {
				border-color: lightblue;
			}
		}

		.result {
			z-index: 100;
			overflow-y: auto;
			position: absolute;
			top: 100%;
			left: 0;
			width: 100%;
			max-height: 486px;
			margin-top: -1px;
			border: solid 1px #dfe6ee;
			background-color: #fff;
		}

		.result__item__select-btn {
			display: flex;
			width: 100%;
			height: 36px;
			color: #222;
			font-size: 0.875rem;
			text-align: left;
			padding: 0 12px;
			align-items: center;
			justify-content: space-between;

			&:hover {
				font-weight: bold;
				padding: 0 11px;
				background-color: #ecf2f8;
			}
		}

		.result__item__select-btn--discontinue:after {
			content: '단종';
			display: block;
			padding: 0 8px;
			background: $btn-blue;
			border-radius: 20px;
			flex-shrink: 0;
			line-height: 30px;
			color: white;
			font-weight: bold;
		}

		.result__item__select-btn__text {
			overflow: hidden;
			text-overflow: ellipsis;
		}

		.selected-side__input--selected {
			border-top-right-radius: 0;
			border-bottom-right-radius: 0;
			border-right: 0;
		}

		.selected-side__delete-btn {
			height: 100%;
			border-radius: 0;
			border-top-right-radius: 4px;
			border-bottom-right-radius: 4px;
			flex-shrink: 0;
		}
	}
</style>
